import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { SubscriptionPlanCode, User } from "common/models/types";
import { grantPremium } from "api/adminSubscriptionService";
import { fetchUsers } from "api/userService";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import Spinner from "components/atoms/Spinner";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { log, LogLevel } from "utils/LogUtil";

interface GrantPremiumSlideProps {
  /** 부여 성공 시 호출 — 부모가 목록/통계 새로고침 + 슬라이드 닫기 */
  onSuccess: () => void;
}

const SEARCH_LIMIT = 30;

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Select = styled.select`
  height: 44px;
  padding: 0 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const UserList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
`;

const UserRow = styled.button<{ $selected?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  background: ${({ $selected }) => ($selected ? "#e0f2fe" : "#fff")};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${({ $selected }) => ($selected ? "#e0f2fe" : "#f9fafb")};
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const GrantPremiumSlide: React.FC<GrantPremiumSlideProps> = ({ onSuccess }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<User[]>([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [planCode, setPlanCode] = useState<SubscriptionPlanCode>("PHAI_PREMIUM_MONTHLY");
  const [months, setMonths] = useState("1");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async () => {
    const term = query.trim();
    if (!term || searching) return;
    setSearching(true);
    setSearched(true);
    try {
      const res = await fetchUsers({ search: term, limit: SEARCH_LIMIT, offset: 0 });
      setResults(res.data || []);
    } catch (error) {
      log(LogLevel.ERROR, "[GrantPremiumSlide] 사용자 검색 실패", error);
      setResults([]);
      ToastManager.show({ content: "사용자 검색에 실패했습니다." });
    } finally {
      setSearching(false);
    }
  };

  const monthsNum = months.trim() === "" ? NaN : Number(months);
  const validMonths = Number.isInteger(monthsNum) && monthsNum >= 1 && monthsNum <= 24;
  const canSubmit = !!selectedUser && validMonths && reason.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedUser) return;
    setSubmitting(true);
    try {
      const res = await grantPremium({
        userId: selectedUser.id,
        planCode,
        months: monthsNum,
        reason: reason.trim(),
      });
      if (res.error === 0) {
        ToastManager.show({ content: "무료 프리미엄이 부여되었습니다.", duration: 3000 });
        onSuccess();
      } else {
        // 409(이미 프리미엄) / 404(없는 유저) / 400(요금제·개월 오류) 등 백엔드 메시지 표기
        ToastManager.show({
          content: res.message || "무료 프리미엄 제공에 실패했습니다.",
          duration: 4000,
        });
        setSubmitting(false);
      }
    } catch (error) {
      log(LogLevel.ERROR, "[GrantPremiumSlide] grant 실패", error);
      ToastManager.show({ content: "무료 프리미엄 제공 중 오류가 발생했습니다.", duration: 4000 });
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Text textType="H3">무료 프리미엄 제공</Text>
      <Hint>결제 없이 지정 기간 동안 프리미엄을 부여합니다. 결제창은 뜨지 않습니다.</Hint>

      <Field>
        <Text textType="Body2Bold">대상 사용자</Text>
        {selectedUser ? (
          <UserRow $selected onClick={() => setSelectedUser(null)}>
            <Text textType="Body2Bold">{selectedUser.name || "(이름 없음)"}</Text>
            <Hint>{selectedUser.email} · 선택 해제하려면 클릭</Hint>
          </UserRow>
        ) : (
          <>
            <Input
              type="text"
              placeholder="이름·전화(정확) / 이메일(부분) 검색 후 Enter"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onEnter={handleSearch}
            />
            <Hint>이름·전화는 정확히 일치해야 하고, 이메일은 일부만 입력해도 됩니다.</Hint>
            {searching ? (
              <Center>
                <Spinner />
              </Center>
            ) : searched && results.length === 0 ? (
              <Hint>일치하는 사용자가 없습니다.</Hint>
            ) : results.length > 0 ? (
              <UserList>
                {results.map((u) => (
                  <UserRow key={u.id} onClick={() => setSelectedUser(u)}>
                    <Text textType="Body2Bold">{u.name || "(이름 없음)"}</Text>
                    <Hint>
                      {u.email}
                      {u.phone ? ` · ${u.phone}` : ""}
                    </Hint>
                  </UserRow>
                ))}
              </UserList>
            ) : null}
          </>
        )}
      </Field>

      <Field>
        <Text textType="Body2Bold">요금제</Text>
        <Select
          value={planCode}
          onChange={(e) => setPlanCode(e.target.value as SubscriptionPlanCode)}
        >
          <option value="PHAI_PREMIUM_MONTHLY">월간</option>
          <option value="PHAI_PREMIUM_YEARLY">연간</option>
        </Select>
        <Hint>권한은 동일하며 표시·기간 산정용입니다.</Hint>
      </Field>

      <Field>
        <Text textType="Body2Bold">제공 개월수 (1~24)</Text>
        <Input
          type="number"
          placeholder="1"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
        />
        {months.length > 0 && !validMonths && <Hint>1~24 사이 정수를 입력하세요.</Hint>}
      </Field>

      <Field>
        <Text textType="Body2Bold">사유</Text>
        <Input
          type="text"
          placeholder="예: 베타 테스터 보상"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>

      <Button
        buttonType="button"
        color="Primary"
        size="Large"
        type="Text"
        variant="Contained"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {submitting ? "처리 중..." : "무료 프리미엄 부여"}
      </Button>
    </Container>
  );
};

export default GrantPremiumSlide;
